import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import {
  ExternalLink,
  Wallet,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Copy,
  Coins,
  BarChart3,
  Shield,
  ShoppingCart,
  Vote,
  Building2,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  INFRACOINN_ADDRESSES,
  CONTRACT_LABELS,
  type InfracoinnContractKey,
  POLYGONSCAN_BASE,
} from '../services/infracoinn-contracts';
import {
  hasMetaMask,
  connectWallet,
  getOMXTokenInfo,
  getStakingInfo,
  stakeOMX,
  claimStakingRewards,
  getRWAAssets,
  polygonscanAddress,
  polygonscanToken,
  type WalletState,
  type OMXTokenInfo,
  type StakingInfo,
  type RWAAsset,
} from '../services/infracoinn.service';

// ── Helpers ──────────────────────────────────────────────────────────────────

function shortAddr(addr: string) {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function copyAddr(addr: string) {
  navigator.clipboard.writeText(addr);
  toast.success('Dirección copiada');
}

// ── Contract row ──────────────────────────────────────────────────────────────

function ContractRow({ contractKey }: { contractKey: InfracoinnContractKey }) {
  const addr = INFRACOINN_ADDRESSES[contractKey];
  const meta = CONTRACT_LABELS[contractKey];

  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        <span className="text-xl">{meta.icon}</span>
        <div>
          <p className="text-sm font-medium">{meta.name}</p>
          <p className="text-xs text-muted-foreground">{meta.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground hidden sm:block">{shortAddr(addr)}</span>
        <button
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => copyAddr(addr)}
          title="Copiar dirección"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <a
          href={contractKey === 'OMX_TOKEN' ? polygonscanToken(addr) : polygonscanAddress(addr)}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          title="Ver en PolygonScan"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function InfracoinnDashboard() {
  const [wallet, setWallet] = useState<WalletState | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [omxInfo, setOmxInfo] = useState<OMXTokenInfo | null>(null);
  const [stakingInfo, setStakingInfo] = useState<StakingInfo | null>(null);
  const [rwaAssets, setRwaAssets] = useState<RWAAsset[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [stakeAmount, setStakeAmount] = useState('');
  const [isStaking, setIsStaking] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  // Load public data on mount (no wallet needed)
  useEffect(() => {
    loadPublicData();
  }, []);

  const loadPublicData = async () => {
    setIsLoadingData(true);
    try {
      const [omx, staking, assets] = await Promise.allSettled([
        getOMXTokenInfo(wallet?.address),
        getStakingInfo(wallet?.address),
        getRWAAssets(),
      ]);

      if (omx.status === 'fulfilled') setOmxInfo(omx.value);
      if (staking.status === 'fulfilled') setStakingInfo(staking.value);
      if (assets.status === 'fulfilled') setRwaAssets(assets.value);
    } catch {
      // silently fail — RPC may have rate limits
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleConnect = async () => {
    if (!hasMetaMask()) {
      toast.error('MetaMask no está instalado. Instálalo en metamask.io');
      return;
    }
    setIsConnecting(true);
    try {
      const state = await connectWallet();
      setWallet(state);
      toast.success(`Wallet conectada: ${shortAddr(state.address)}`);
      // Reload with wallet to get balances
      const [omx, staking] = await Promise.allSettled([
        getOMXTokenInfo(state.address),
        getStakingInfo(state.address),
      ]);
      if (omx.status === 'fulfilled') setOmxInfo(omx.value);
      if (staking.status === 'fulfilled') setStakingInfo(staking.value);
    } catch (err) {
      toast.error(`Error al conectar: ${(err as Error).message}`);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleStake = async () => {
    if (!wallet) { toast.error('Conecta tu wallet primero'); return; }
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) { toast.error('Introduce una cantidad válida'); return; }
    setIsStaking(true);
    try {
      const hash = await stakeOMX(stakeAmount);
      toast.success(`Staking realizado. TX: ${shortAddr(hash)}`);
      setStakeAmount('');
      await loadPublicData();
    } catch (err) {
      toast.error(`Error en staking: ${(err as Error).message}`);
    } finally {
      setIsStaking(false);
    }
  };

  const handleClaimRewards = async () => {
    if (!wallet) { toast.error('Conecta tu wallet primero'); return; }
    setIsClaiming(true);
    try {
      const hash = await claimStakingRewards();
      toast.success(`Rewards reclamados. TX: ${shortAddr(hash)}`);
      await loadPublicData();
    } catch (err) {
      toast.error(`Error al reclamar: ${(err as Error).message}`);
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">InfraCoinn</h2>
          <p className="text-muted-foreground text-sm">
            Protocolo DeFi de activos reales tokenizados · Polygon Mainnet
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadPublicData}
            disabled={isLoadingData}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingData ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          {wallet ? (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20 flex items-center gap-1.5 px-3 py-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {shortAddr(wallet.address)}
            </Badge>
          ) : (
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={isConnecting}
              className="bg-primary hover:bg-primary/90"
            >
              {isConnecting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Conectando...</>
              ) : (
                <><Wallet className="mr-2 h-4 w-4" />Conectar MetaMask</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-yellow-500" />
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Coins className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">OMX Supply</p>
                {omxInfo ? (
                  <p className="text-lg font-bold">
                    {parseFloat(omxInfo.totalSupply).toLocaleString('es', { maximumFractionDigits: 0 })}
                  </p>
                ) : (
                  <div className="h-6 w-20 bg-muted animate-pulse rounded" />
                )}
              </div>
            </div>
            {wallet && omxInfo?.balance && (
              <p className="text-xs text-muted-foreground mt-2">
                Tu balance: <span className="text-foreground font-medium">{parseFloat(omxInfo.balance).toLocaleString('es', { maximumFractionDigits: 2 })} OMX</span>
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-purple-500" />
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">APR Staking</p>
                {stakingInfo ? (
                  <p className="text-lg font-bold text-purple-500">{stakingInfo.apr}%</p>
                ) : (
                  <div className="h-6 w-16 bg-muted animate-pulse rounded" />
                )}
              </div>
            </div>
            {stakingInfo && (
              <p className="text-xs text-muted-foreground mt-2">
                Total staked: <span className="text-foreground font-medium">{parseFloat(stakingInfo.totalStaked).toLocaleString('es', { maximumFractionDigits: 0 })} OMX</span>
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500" />
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Activos RWA</p>
                <p className="text-lg font-bold">{rwaAssets.length}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {rwaAssets.filter(a => a.certified).length} certificados
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-500" />
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Contratos</p>
                <p className="text-lg font-bold">11</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
              Polygon Mainnet activos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="contracts">
        <TabsList>
          <TabsTrigger value="contracts">
            <Shield className="h-3.5 w-3.5 mr-1.5" />Contratos
          </TabsTrigger>
          <TabsTrigger value="staking">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" />Staking
          </TabsTrigger>
          <TabsTrigger value="assets">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />Activos RWA
          </TabsTrigger>
          <TabsTrigger value="governance">
            <Vote className="h-3.5 w-3.5 mr-1.5" />Gobernanza
          </TabsTrigger>
          <TabsTrigger value="marketplace">
            <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />Marketplace
          </TabsTrigger>
        </TabsList>

        {/* ── Contracts tab ── */}
        <TabsContent value="contracts">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
            <CardHeader>
              <CardTitle className="text-base">Contratos desplegados en Polygon Mainnet</CardTitle>
              <CardDescription>11 contratos verificados · Chain ID 137</CardDescription>
            </CardHeader>
            <CardContent>
              {(Object.keys(INFRACOINN_ADDRESSES) as InfracoinnContractKey[]).map(key => (
                <ContractRow key={key} contractKey={key} />
              ))}
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Verificados en PolygonScan · Polygon Mainnet (Chain 137)
                </p>
                <a
                  href={`${POLYGONSCAN_BASE}/address/${INFRACOINN_ADDRESSES.RWA_FACTORY}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Ver Factory en PolygonScan
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Staking tab ── */}
        <TabsContent value="staking">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="relative overflow-hidden border-border">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-purple-500" />
              <CardHeader>
                <CardTitle className="text-base">Hacer Staking de OMX</CardTitle>
                <CardDescription>Gana recompensas bloqueando tokens OMX</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!wallet && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Conecta MetaMask para hacer staking</span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Cantidad OMX a stakear</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="100"
                      value={stakeAmount}
                      onChange={e => setStakeAmount(e.target.value)}
                      disabled={!wallet}
                    />
                    <Button
                      onClick={handleStake}
                      disabled={!wallet || isStaking || !stakeAmount}
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                    >
                      {isStaking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Stakear'}
                    </Button>
                  </div>
                </div>

                {wallet && stakingInfo && (
                  <div className="rounded-lg border p-3 space-y-2 bg-accent/30">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tu staking:</span>
                      <span className="font-medium">{parseFloat(stakingInfo.userStaked || '0').toLocaleString('es', { maximumFractionDigits: 2 })} OMX</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Rewards pendientes:</span>
                      <span className="font-medium text-green-500">{parseFloat(stakingInfo.pendingRewards || '0').toLocaleString('es', { maximumFractionDigits: 4 })} OMX</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleClaimRewards}
                      disabled={isClaiming || parseFloat(stakingInfo.pendingRewards || '0') === 0}
                    >
                      {isClaiming ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Reclamando...</> : 'Reclamar Rewards'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-border">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-purple-500" />
              <CardHeader>
                <CardTitle className="text-base">Estadísticas del Pool</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stakingInfo ? (
                  <>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">APR actual</span>
                      <span className="font-bold text-purple-500">{stakingInfo.apr}%</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">Total staked</span>
                      <span className="font-medium">{parseFloat(stakingInfo.totalStaked).toLocaleString('es', { maximumFractionDigits: 0 })} OMX</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-sm text-muted-foreground">Contrato</span>
                      <a href={polygonscanAddress(INFRACOINN_ADDRESSES.STAKING)} target="_blank" rel="noopener noreferrer" className="text-primary text-xs flex items-center gap-1 hover:underline">
                        {shortAddr(INFRACOINN_ADDRESSES.STAKING)}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => <div key={i} className="h-8 bg-muted animate-pulse rounded" />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── RWA Assets tab ── */}
        <TabsContent value="assets">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500" />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Activos Tokenizados (RWA)</CardTitle>
                  <CardDescription>Activos reales registrados en InfracoinnRWAFactory</CardDescription>
                </div>
                <a href={`/dashboard/assets/new`}>
                  <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white">
                    <Building2 className="mr-1.5 h-3.5 w-3.5" />
                    Tokenizar activo
                  </Button>
                </a>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingData ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded" />)}
                </div>
              ) : rwaAssets.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No hay activos registrados aún</p>
                  <p className="text-xs text-muted-foreground mt-1">Sé el primero en tokenizar un activo real</p>
                </div>
              ) : (
                <ScrollArea className="h-80">
                  {rwaAssets.map((asset, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {asset.symbol.slice(0, 2)}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{asset.name}</p>
                          <p className="text-xs text-muted-foreground">{asset.symbol} · {shortAddr(asset.owner)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {asset.certified ? (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">
                            <CheckCircle2 className="mr-1 h-3 w-3" />Certificado
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Pendiente</Badge>
                        )}
                        <a href={polygonscanAddress(asset.contractAddr)} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Governance tab ── */}
        <TabsContent value="governance">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-indigo-500" />
            <CardHeader>
              <CardTitle className="text-base">Gobernanza InfraCoinn</CardTitle>
              <CardDescription>Vota en propuestas del protocolo usando tus tokens OMX</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 flex gap-2 text-sm">
                <Vote className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Contrato de Gobernanza</p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Propuesta y votación on-chain. Cada OMX = 1 voto.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between py-2 border rounded-lg px-4">
                <span className="text-sm text-muted-foreground">Contrato Governance</span>
                <a href={polygonscanAddress(INFRACOINN_ADDRESSES.GOVERNANCE)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                  {shortAddr(INFRACOINN_ADDRESSES.GOVERNANCE)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="flex items-center justify-between py-2 border rounded-lg px-4">
                <span className="text-sm text-muted-foreground">Contrato Timelock</span>
                <a href={polygonscanAddress(INFRACOINN_ADDRESSES.TIMELOCK)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                  {shortAddr(INFRACOINN_ADDRESSES.TIMELOCK)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <p className="text-xs text-muted-foreground text-center pt-2">
                La interfaz de votación estará disponible próximamente. Mientras tanto, puedes interactuar directamente desde PolygonScan.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Marketplace tab ── */}
        <TabsContent value="marketplace">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-emerald-500" />
            <CardHeader>
              <CardTitle className="text-base">Marketplace de Activos</CardTitle>
              <CardDescription>Compra y vende fracciones de activos tokenizados</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Marketplace próximamente</p>
                <p className="text-xs text-muted-foreground mt-1">El contrato está desplegado y activo en Polygon</p>
                <a href={polygonscanAddress(INFRACOINN_ADDRESSES.MARKETPLACE)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Ver contrato en PolygonScan
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
